"use client";
import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { Section } from "@/types/common";
import Link from "next/link";
import { adsService } from "@/services/adsService";
import { usePathname } from "next/navigation";

// Device type detection utility
const getDeviceType = (): string => {
  if (typeof window === 'undefined') return 'unknown';

  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
    return 'mobile';
  } else if (ua.includes('tablet') || ua.includes('ipad')) {
    return 'tablet';
  } else {
    return 'desktop';
  }
};

// Get viewport dimensions
const getViewportDimensions = () => {
  if (typeof window === 'undefined') return { width: 0, height: 0 };
  return {
    width: window.innerWidth,
    height: window.innerHeight
  };
};

// Detect whether a banner URL points to a video file (ignoring query/hash).
const isVideoUrl = (url?: string | null): boolean => {
  if (!url) return false;
  const clean = url.split('?')[0].split('#')[0].toLowerCase();
  return /\.(mp4|webm|ogg|ogv|mov|m4v)$/.test(clean);
};

// Renders a single ad banner as a video when the URL is a video file,
// otherwise as an image.
const AdMedia = ({ src, className }: { src: string; className?: string }) => {
  if (isVideoUrl(src)) {
    return (
      <video
        src={src}
        className={className}
        controls
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
      />
    );
  }
  // eslint-disable-next-line @next/next/no-img-element
  return <img src={src} alt="Ad banner" className={className} />;
};

export const AdsSection = ({ data }: { data: Section }) => {
  const adRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const prevPathnameRef = useRef<string | null>(null);
  console.log('data AdsSection=>',data)

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [hasTrackedView, setHasTrackedView] = useState(false);
  const [isSessionReady, setIsSessionReady] = useState(false);
  const [viewportDimensions, setViewportDimensions] = useState(getViewportDimensions());
  const durationRef = useRef(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollTimeRef = useRef(0);
  const lastHoverTimeRef = useRef(0);
  const lastSentTimeRef = useRef(0); // Track the last time we sent a time tracking event

  const isRunningRef = useRef(false);

  // Extract banner_id and campaign_id once from banner_data - memoized to react to data changes
  const { banner, campaign_id, banner_id } = useMemo(() => {
    console.log("🔍 [AdsSection] Extracting banner data:", {
      hasData: !!data,
      hasBannerData: !!data?.banner_data,
      bannerDataLength: data?.banner_data?.length,
      bannerData: data?.banner_data,
      firstBanner: data?.banner_data?.[0]
    });

    const bannerData = data?.banner_data && data.banner_data.length > 0 ? data.banner_data[0] : null;

    // Try to get numeric IDs from banner_data, fallback to section level if needed
    const extracted = {
      banner: bannerData,
      campaign_id: bannerData?.campaign_id != null ? Number(bannerData.campaign_id) : undefined,
      banner_id: bannerData?.banner_id != null ? Number(bannerData.banner_id) : undefined
    };

    console.log("✅ [AdsSection] Extracted values:", {
      banner: extracted.banner,
      campaign_id: extracted.campaign_id,
      banner_id: extracted.banner_id,
      campaign_idType: typeof extracted.campaign_id,
      banner_idType: typeof extracted.banner_id,
      rawCampaignId: bannerData?.campaign_id,
      rawBannerId: bannerData?.banner_id,
      sectionBannerId: data?.banner_id
    });

    return extracted;
  }, [data]);

  // Update viewport dimensions on resize
  useEffect(() => {
    const handleResize = () => {
      setViewportDimensions(getViewportDimensions());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const urlWithoutQuery = window.location.origin + pathname;

    const globalSessionKey = `ads_session_${urlWithoutQuery}`;
    const isSessionInProgress = sessionStorage.getItem(globalSessionKey);

    if (isSessionInProgress === 'in_progress') return;

    if (urlWithoutQuery === localStorage.getItem("page_url_full_path")) {
      // Load existing session from localStorage
      const existingSessionId = localStorage.getItem("page_session_id");
      if (existingSessionId && !sessionId) {
        setSessionId(existingSessionId);
        setIsSessionReady(true);
      }
      return;
    }

    if (prevPathnameRef.current === pathname) return;

    if (sessionId) return;
    // if (localStorage.getItem("page_session_id")) return;

    if (!data || !banner) {
      if (data && data.banner_data !== undefined && data.banner_data !== null && !Array.isArray(data.banner_data)) {

      console.warn("⚠️ [useEffect Session] No banner data available for session generation, skipping", {
        hasData: !!data,
        hasBannerData: !!data?.banner_data,
        banner,
        campaign_id,
        banner_id
      });
      }
      return;
    }

    // console.log("Starting session generation for URL:", urlWithoutQuery);

    sessionStorage.setItem(globalSessionKey, 'in_progress');
    isRunningRef.current = true;
    prevPathnameRef.current = pathname;

    setHasTrackedView(false);
    durationRef.current = 0;
    lastSentTimeRef.current = 0; // Reset last sent time
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    (async () => {
      try {
        // console.log("Generating ads session for URL:", urlWithoutQuery, data.banner_data);
        const res = await adsService.generatAdsSessions(urlWithoutQuery);
        // console.log("Session generation response:", res);
        const id = res?.data?.session_id;
        if (res?.success && id) {
          localStorage.setItem("page_url_full_path", urlWithoutQuery);
          localStorage.setItem("page_session_id", id);
          // console.log("Setting session ID:", id);
          setSessionId(id);
          setIsSessionReady(true);
        } else {
          console.error("Failed to generate session:", res);
        }
      } catch (err) {
        console.error("Error creating session:", err);
      } finally {
        isRunningRef.current = false;
        sessionStorage.removeItem(globalSessionKey);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, sessionId]);

  useEffect(() => {
    // console.log("Banner data effect triggered", {
    //   sessionId: localStorage.getItem("page_session_id"),
    //   hasBannerData: !!data?.banner_data?.length,
    //   bannerDataLength: data?.banner_data?.length,
    //   isRunning: isRunningRef.current
    // });

    if (!sessionId && banner && !isRunningRef.current) {
      const urlWithoutQuery = window.location.origin + pathname;
      const globalSessionKey = `ads_session_${urlWithoutQuery}`;
      const isSessionInProgress = sessionStorage.getItem(globalSessionKey);

      if (isSessionInProgress === 'in_progress') return;
      if (urlWithoutQuery === localStorage.getItem("page_url_full_path")) return;

      // console.log("Banner data available, triggering session generation for URL:", urlWithoutQuery);

      sessionStorage.setItem(globalSessionKey, 'in_progress');
      isRunningRef.current = true;

      (async () => {
        try {
          // console.log("Generating ads session for URL:", urlWithoutQuery, data.banner_data);
          const res = await adsService.generatAdsSessions(urlWithoutQuery);
          // console.log("Session generation response:", res);
          const id = res?.data?.session_id;
          if (res?.success && id) {
            localStorage.setItem("page_url_full_path", urlWithoutQuery);
            localStorage.setItem("page_session_id", id);
            // console.log("Setting session ID:", id);
            setSessionId(id);
            setIsSessionReady(true);
          } else {
            console.error("Failed to generate session:", res);
          }
        } catch (err) {
          console.error("Error creating session:", err);
        } finally {
          isRunningRef.current = false;
          sessionStorage.removeItem(globalSessionKey);
        }
      })();
    }
  }, [banner, sessionId, pathname]);

  useEffect(() => {
    if (!sessionId || !isSessionReady) {
      // console.log("Session not ready for intersection observer", { sessionId, isSessionReady });
      return;
    }
    // if (!sessionId || !isSessionReady) {
    //   console.log("Session not ready for intersection observer", { sessionId, isSessionReady });
    //   return;
    // }

    if (!data || !banner) {
      if (data && data.banner_data !== undefined && data.banner_data !== null && !Array.isArray(data.banner_data)) {
      console.warn("⚠️ [useEffect IntersectionObserver] No banner data available for intersection observer", {
        sessionId: localStorage.getItem("page_session_id"),
        data,
        bannerData: data?.banner_data,
        banner,
        campaign_id,
        banner_id
      });
      }
      return;
    }

    const urlWithoutQuery = window.location.origin + pathname;
    const globalSessionKey = `ads_session_${urlWithoutQuery}`;
    const isSessionInProgress = sessionStorage.getItem(globalSessionKey);

    if (isSessionInProgress === 'in_progress') {
      // console.log("Session generation in progress, skipping intersection observer setup");
      return;
    }

    // console.log("Session ready and banner data available, setting up intersection observer:", sessionId, localStorage.getItem("page_session_id"));

    const setupObserver = (): IntersectionObserver | null => {
      const node = adRef.current;
      if (!node) {
        // console.log("DOM node not found, retrying...");
        return null;
      }

      // console.log("Setting up intersection observer for sessionId:", sessionId, localStorage.getItem("page_session_id"));

      const rect = node.getBoundingClientRect();
      const isVisibleOnMount = rect.top < window.innerHeight && rect.bottom > 0;
      if (isVisibleOnMount && !hasTrackedView) {
        console.log("Ad visible on mount, sending tracking");
        sendTracking({
          event_type: 'impression',
          is_loaded: true,
          is_in_viewport: true,
          viewport_duration: 0
        });
        setHasTrackedView(true);
        startTimer();
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          // console.log("Intersection observer triggered", {
          //   isIntersecting: entry.isIntersecting,
          //   hasTrackedView,
          //   intersectionRatio: entry.intersectionRatio
          // });
          console.log("Intersection observer triggered", {
            isIntersecting: entry.isIntersecting,
            hasTrackedView,
            intersectionRatio: entry.intersectionRatio
          });
          if (entry.isIntersecting && !hasTrackedView) {
            console.log("Ad entered viewport, sending tracking");
            sendTracking({
              event_type: 'impression',
              is_loaded: true,
              is_in_viewport: true,
              viewport_duration: durationRef.current,
            });
            setHasTrackedView(true);
            startTimer();
          } else if (!entry.isIntersecting) {
            console.log("Ad left viewport, stopping timer");
            // Send remaining time that hasn't been tracked yet (incremental)
            const remainingTime = durationRef.current - lastSentTimeRef.current;
            if (remainingTime > 0) {
              sendTracking({
                event_type: 'time',
                is_in_viewport: false,
                viewport_duration: remainingTime // Send only remaining incremental time
              });
            }
            stopTimer();
          }
        },
        { threshold: 0.5 }
      );

      observer.observe(node);
      return observer;
    };

    let observer: IntersectionObserver | null = setupObserver();

    if (!observer) {
      let retryCount = 0;
      const maxRetries = 5;

      const retrySetup = () => {
        retryCount++;
        // console.log(`Retry attempt ${retryCount}/${maxRetries}`);

        observer = setupObserver();
        if (!observer && retryCount < maxRetries) {
          setTimeout(retrySetup, 200 * retryCount);
        } else if (!observer) {
          console.error("Failed to setup intersection observer after all retries");
        }
      };

      setTimeout(retrySetup, 200);

      return () => {
        if (observer) {
          observer.disconnect();
        }
        stopTimer();
      };
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
      stopTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, isSessionReady, banner]);

  useEffect(() => {
    if (data?.banner_id && sessionId) {
      // console.log("Banner data changed, resetting tracking state", data);
      setHasTrackedView(false);
      durationRef.current = 0;
      lastSentTimeRef.current = 0; // Reset last sent time
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [data?.banner_id, sessionId]);

  // Scroll tracking
  useEffect(() => {
    // if (!localStorage.getItem("page_session_id") || !isSessionReady) return;
    if (!sessionId || !isSessionReady) return;

    const handleScroll = () => {
      const now = Date.now();
      if (now - lastScrollTimeRef.current > 1000) { // Throttle to once per second
        lastScrollTimeRef.current = now;
        sendUserTracking({
          event_type: 'scroll',
          seconds: Math.floor(now / 1000),
          url: window.location.href,
          referrer_url: document.referrer
        });
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, isSessionReady]);

  // Click handler: pass element and text
  const handleClick = (e?: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    // Prevent event bubbling to avoid duplicate clicks
    e?.stopPropagation();
    
    // console.log("Ad clicked, sending click tracking");
    const element = e?.currentTarget;
    const now = Date.now();
    
    // Send single combined tracking call for click
    sendUserTracking({
      event_type: 'click',
      seconds: Math.floor(now / 1000),
      url: window.location.href,
      referrer_url: document.referrer,
      element: element ? element.tagName : undefined,
      text: element ? (element.textContent || undefined) : undefined
    });
  };

  // Hover handler: pass element and text
  const handleMouseEnter = (e?: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const now = Date.now();
    if (now - lastHoverTimeRef.current > 500) { // Throttle hover events
      lastHoverTimeRef.current = now;
      const element = e?.currentTarget;
      sendUserTracking({
        event_type: 'hover',
        seconds: Math.floor(now / 1000),
        url: window.location.href,
        referrer_url: document.referrer,
        element: element ? element.tagName : undefined,
        text: element ? (element.textContent || undefined) : undefined
      });
    }
  };

  const sendTracking = useCallback(async (flags: {
    event_type?: string;
    is_loaded?: boolean;
    is_in_viewport?: boolean;
    is_click?: boolean;
    viewport_duration?: number;
    element?: string;
    text?: string;
  }) => {
    console.log("📤 [sendTracking] Called with flags:", flags);
    console.log("📤 [sendTracking] Current state:", {
      sessionId: localStorage.getItem("page_session_id"),
      hasBanner: !!banner,
      campaign_id,
      banner_id,
      campaign_idType: typeof campaign_id,
      banner_idType: typeof banner_id,
      campaign_idUndefined: campaign_id === undefined,
      banner_idUndefined: banner_id === undefined,
      data: data,
      bannerData: data?.banner_data
    });
    
    if (!sessionId || !banner || campaign_id === undefined || banner_id === undefined) {
      console.warn("⚠️ [sendTracking] SKIPPED - Missing required data:", {
        hasSessionId: !!sessionId,
        hasBanner: !!banner,
        campaign_id,
        banner_id,
        campaign_idUndefined: campaign_id === undefined,
        banner_idUndefined: banner_id === undefined
      });
      return;
    }

    const trackingData = {
      session_id: sessionId,
      campaign_id,
      banner_id,
      event_type: flags.event_type || 'impression',
      full_url: window.location.href,
      referrer_url: document.referrer,
      user_agent: navigator.userAgent,
      device_type: getDeviceType(),
      viewport_width: viewportDimensions.width,
      viewport_height: viewportDimensions.height,
      ...flags,
    };

    console.log("📤 [sendTracking] Sending tracking data:", trackingData);
    console.log("📤 [sendTracking] Tracking data details:", {
      session_id: trackingData.session_id,
      campaign_id: trackingData.campaign_id,
      banner_id: trackingData.banner_id,
      campaign_idType: typeof trackingData.campaign_id,
      banner_idType: typeof trackingData.banner_id
    });

    try {
      const response = await adsService.adsTracking(trackingData);
      console.log("✅ [sendTracking] Tracking response:", response);
    } catch (err) {
      console.error("❌ [sendTracking] Tracking failed:", err);
    }
  }, [sessionId, banner, campaign_id, banner_id, viewportDimensions, data]);

  const sendUserTracking = useCallback(async (userData: {
    event_type: string;
    seconds?: number;
    url?: string;
    referrer_url?: string;
    element?: string;
    text?: string;
  }) => {
    console.log("👤 [sendUserTracking] Called with userData:", userData);
    console.log("👤 [sendUserTracking] Current state:", {
      sessionId,
      hasBanner: !!banner,
      campaign_id,
      banner_id,
      campaign_idType: typeof campaign_id,
      banner_idType: typeof banner_id,
      campaign_idUndefined: campaign_id === undefined,
      banner_idUndefined: banner_id === undefined,
      bannerData: data?.banner_data?.[0],
      sectionData: data
    });

    if (!sessionId || !banner || campaign_id === undefined || banner_id === undefined) {
      console.warn("⚠️ [sendUserTracking] SKIPPED - Missing required data:", {
        hasSessionId: !!sessionId,
        hasBanner: !!banner,
        campaign_id,
        banner_id,
        campaign_idUndefined: campaign_id === undefined,
        banner_idUndefined: banner_id === undefined,
        bannerData: data?.banner_data?.[0],
        sectionBannerId: data?.banner_id
      });
      return;
    }

    const userTrackingData = {
      session_id: sessionId,
      campaign_id,
      banner_id,
      event_type: userData.event_type,
      url: userData.url || window.location.href,
      referrer_url: userData.referrer_url || document.referrer,
      seconds: userData.seconds,
      element: userData.element,
      text: userData.text,
      user_agent: navigator.userAgent,
      device_type: getDeviceType(),
      viewport_width: viewportDimensions.width,
      viewport_height: viewportDimensions.height,
    };

    console.log("👤 [sendUserTracking] Sending user tracking data:", userTrackingData);
    console.log("👤 [sendUserTracking] User tracking data details:", {
      session_id: userTrackingData.session_id,
      campaign_id: userTrackingData.campaign_id,
      banner_id: userTrackingData.banner_id,
      campaign_idType: typeof userTrackingData.campaign_id,
      banner_idType: typeof userTrackingData.banner_id
    });

    try {
      const response = await adsService.adsTracking(userTrackingData);
      console.log("✅ [sendUserTracking] User tracking response:", response);
    } catch (err) {
      console.error("❌ [sendUserTracking] User tracking failed:", err);
    }
  }, [sessionId, banner, campaign_id, banner_id, viewportDimensions, data]);

  const startTimer = useCallback(() => {
    if (!timerRef.current) {
      timerRef.current = setInterval(() => {
        durationRef.current += 1;

        // Send incremental time tracking every 30 seconds (send 30 seconds each time)
        if (durationRef.current % 30 === 0) {
          sendUserTracking({
            event_type: 'time',
            seconds: 30, // Send incremental time (30 seconds), not cumulative
            url: window.location.href,
            referrer_url: document.referrer
          });
          lastSentTimeRef.current = durationRef.current; // Track when we last sent
        }
      }, 1000);
    }
  }, [sendUserTracking]);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  console.log("🎨 [AdsSection] Render check:", {
    hasData: !!data,
    hasBanner: !!banner,
    banner,
    campaign_id,
    banner_id,
    bannerData: data?.banner_data,
    bannerDataLength: data?.banner_data?.length
  });

  if (!data || !banner) {
    if (data && data.banner_data !== undefined && data.banner_data !== null && !Array.isArray(data.banner_data)) {
    console.warn("⚠️ [AdsSection] Component returning null - no banner data", {
      data,
      bannerData: data?.banner_data,
      hasBannerData: !!data?.banner_data,
      isArray: Array.isArray(data?.banner_data),
      banner,
      campaign_id,
      banner_id
    });
    }
    return null;
  }

  return (
    <div
      ref={adRef}
      className={`w-full max-w-7xl mx-auto sm:px-6 lg:px-0 ${data.banner_id === "home-banner-1_1" ? "mt-10" : ""}
        ${data.banner_id === "home-banner-2_2" ? "pt-0" : ""}`}
      data-template={data.template}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      style={{ minHeight: '1px' }} // Ensure the element has some height
    >
      <Link
        href={banner.target_url || "#"}
        target={banner.target_url ? "_blank" : undefined}
        rel={banner.target_url ? "noopener noreferrer" : undefined}
        prefetch={false}
      >
        {/* {JSON.stringify(data)} */}
        {/* Mobile banner (<=768px): video or image */}
        <div className="block md:hidden">
          <AdMedia src={banner.mobile_banner_image || banner.desktop_banner_image} />
        </div>
        {/* Desktop banner (>768px): video or image */}
        <div className="hidden md:block">
          <AdMedia src={banner.desktop_banner_image || banner.mobile_banner_image} />
        </div>
      </Link>
    </div>
  );
};
